import FmdGoodIcon from "@mui/icons-material/FmdGood";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import { Box, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";

import { CompanyBusinessTypeChip } from "components/CompanyBusinessTypeChip/CompanyBusinessTypeChip";
import { ShipmentTypeChip } from "components/ShipmentTypeChip";
import { ShipmentEquipmentTypeText } from "components/Texts/ShipmentEquipmentTypeText";
import { ShipmentFootageText } from "components/Texts/ShipmentFootageText";
import { ShipmentIDText } from "components/Texts/ShipmentIDText";
import { ShipmentLocationText } from "components/Texts/ShipmentLocationText";
import { ShipmentWeightText } from "components/Texts/ShipmentWeightText";
import { useCarrierShipmentPageContext, useSubscriptionStatus } from "hooks";
import {
  carrierHasBookingWriteAccess,
  getServiceTypeDisplayLabel,
  scrollMainContentToTop,
  shouldShowShipperCompanyName,
  toCityProvinceCountryAndZipReadablePointLocation,
} from "utils";

import { EVOTRUX_BLUE, EVOTRUX_GREEN } from "../../../../constants";
import { CompanyNameUpgradeText } from "../CompanyNameUpgradeText";
import { MultiShipmentProjectLoadsAvailableLoadsLabel } from "../MultiShipmentProjectLoadsAvailableLoadsLabel";
import {
  QuotedShipmentChip,
  QuotedShipmentTeammateChip,
} from "../QuotedShipmentChip";

interface Props {
  shipment: CarrierCondensedShipment;
  selected: boolean;
  select: (id: number) => void;
  selectionIsDisabled: boolean;
}

export function CompactShipmentPreview(props: Props) {
  const { shipment, selected, select, selectionIsDisabled } = props;
  const { onRefresh } = useCarrierShipmentPageContext();
  const { isPayingCarrier } = useSubscriptionStatus();
  const showCompanyName = shouldShowShipperCompanyName({
    isSubscribed: Boolean(isPayingCarrier),
    isPartnerShipment: shipment.is_partner_shipment,
    isBookedByCarrier: carrierHasBookingWriteAccess(shipment),
  });

  const shipmentInfo = shipment.info;
  const equipmentTypes = shipment?.equipments;
  const bg = selected ? "#e3e3e3" : "white";
  const getEquipmentTypesDisplay = () => {
    let equipmentTypesDisplay = "-";
    if (equipmentTypes.length > 1) {
      equipmentTypesDisplay = equipmentTypes[0]?.name;
      /*
        This will result in a string that looks like:
        "Dry Van (+ 2 more)"
        Indicating that the shipment has a dry van equipment specified, plus 2 additional types.
      */
      equipmentTypesDisplay = `${equipmentTypesDisplay} (+ ${equipmentTypes.length - 1} more)`;
    } else if (equipmentTypes.length === 1) {
      equipmentTypesDisplay = equipmentTypes[0]?.name;
    }
    return equipmentTypesDisplay;
  };

  return (
    <Box
      component={selectionIsDisabled ? "div" : Link}
      to={selectionIsDisabled ? undefined : `/shipments/${shipmentInfo.id}`}
      p="1rem"
      sx={{
        display: "block",
        textDecoration: "none",
        color: "inherit",
        "&:hover": {
          bgcolor: selectionIsDisabled ? "#959696" : "#f8f8f8",
          cursor: selectionIsDisabled ? "wait" : "pointer",
        },
        background: bg,
        ...(shipment?.is_partner_shipment && {
          borderLeft: `4px solid ${EVOTRUX_GREEN}`,
          backgroundColor: selected ? "#f1f8e9" : "transparent",
        }),
      }}
      onClick={(e: React.MouseEvent) => {
        if (!selectionIsDisabled) {
          if (onRefresh) {
            onRefresh();
          }
          select(shipmentInfo.id);
          scrollMainContentToTop();
        } else {
          e.preventDefault();
        }
      }}
    >
      <Box mb="0.5rem">
        <ShipmentEquipmentTypeText
          data={[
            {
              name: getEquipmentTypesDisplay()
                .toLowerCase()
                .split("_")
                .join(" "),
            } as Equipment,
          ]}
        />
        <MultiShipmentProjectLoadsAvailableLoadsLabel shipment={shipment} />
        <Typography sx={{ textTransform: "capitalize" }}>
          {getServiceTypeDisplayLabel(shipmentInfo.service)}
        </Typography>
        {showCompanyName ? (
          <Typography color={EVOTRUX_BLUE}>
            <b>{shipment?.shipment_company?.name || ""}</b>
          </Typography>
        ) : (
          <CompanyNameUpgradeText />
        )}
      </Box>
      <Stack direction="row" width="100%" sx={{ flexWrap: "wrap", gap: 2 }}>
        <ShipmentWeightText
          data={parseFloat(shipmentInfo.weight_lb.toString())}
        />
        {shipmentInfo.footage && (
          <ShipmentFootageText
            data={parseFloat(shipmentInfo.footage.toString())}
          />
        )}
      </Stack>
      <Stack
        direction="row"
        alignItems="stretch"
        sx={{ flexWrap: "wrap", gap: 1 }}
      >
        <ShipmentIDText shipment={shipment} />
        <QuotedShipmentChip
          quoted={shipment?.has_carrier_active_quote ?? false}
        />
        <QuotedShipmentTeammateChip
          teammateQuotes={shipment?.teammate_quotes}
        />
        <ShipmentTypeChip shipmentType={shipment?.info?.type} />
        <CompanyBusinessTypeChip
          businessType={shipment.shipment_company.business_type}
        />
      </Stack>
      <Stack spacing={1} mt="1rem" flexWrap="wrap">
        <ShipmentLocationText
          data={toCityProvinceCountryAndZipReadablePointLocation(
            shipment.first_point.point_location,
          )}
          icon={
            <TripOriginIcon sx={{ fontSize: "0.9rem", marginRight: "5px" }} />
          }
        />
        <ShipmentLocationText
          data={toCityProvinceCountryAndZipReadablePointLocation(
            shipment.last_point.point_location,
          )}
          icon={<FmdGoodIcon sx={{ fontSize: "0.9rem", marginRight: "5px" }} />}
        />
      </Stack>
    </Box>
  );
}
